// The one layout every authenticated screen uses (design spec 2026-10-19 §2; decision 2026-10-19):
// `@companyos/ui`'s AppShell with the global rail, the mode tabs centred in the top bar, the
// mode's sidebar, the side panel and, on phones, the bottom mode bar. Three variants:
// - CompanyShell (`/c/$slug/…`): everything, with the focus chips, `1`–`6`, `g` chords and ⌘K.
// - OperatorShell (`/operator…`, agency users): the rail (Operator active) and its sidebar.
// - PlainShell (company picker, unknown company): the top bar only.
// The Inbox is a rail item under Desk that opens its page (`/c/{slug}/inbox`); hovering it (a long
// press on touch) opens its quick peek (inbox-peek.tsx; SHELL-DECISIONS 2026-10-30: one Inbox
// entry, the top bar's bell is retired, as was the M2-07 drawer).
import { AppShell, useIsMobile } from "@companyos/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useRouter, useRouterState } from "@tanstack/react-router";
import { type ReactNode, useEffect, useMemo, useRef } from "react";
import type { Api } from "../api/client";
import type { MeResponse } from "../api/types";
import { focusSearch, modeLanding } from "../extensions/mode-switch";
import { canOpen, modeAccess, modeByKey, shownModes } from "../extensions/modes";
import { BreakGlassBanner } from "../features/agency/company-break-glass";
import { type CompanyAccess, companiesOf, isManager, isOperator } from "../features/company";
import { FlowBanner } from "../features/flows/flow-entry";
import { AskHost, useAsk } from "../features/insights/ask-host";
import { QuickAddHost, useQuickAdd } from "../features/quick-add/quick-add-host";
import { MODE_LABEL, setupEntries } from "../features/settings/setup-catalog";
import { featureState } from "../features/use-feature";
import { TimerChip } from "../features/work/task-time";
import { FocusProvider, useFocus } from "../focus/focus";
import { browserPush } from "../pwa/push";
import { type GoShortcut, useGoShortcuts } from "../shortcuts/go-shortcuts";
import { type ModeKey, useModeKeys } from "../shortcuts/mode-keys";
import { MobileAppsMenu, SidebarApps } from "./app-items";
import { CommandMenu, type CommandMode, type CommandPage } from "./command-menu";
import { commandPages } from "./command-registry";
import "./default-commands";
import { CompanySwitcher } from "./company-switcher";
import { DesktopNotifier } from "./desktop-notifier";
import { FocusChips, focusControlsAt } from "./focus-chips";
import { InboxBottomItem, InboxRailItem, isInboxPage } from "./inbox-peek";
import { MeMenu } from "./me-menu";
import { PaletteHits } from "./palette-hits";
import { WorkPaletteExtras } from "./palette-work";
import { PushOpen } from "./push-open";
import { MobileModeBar, ShellModeTabs, ShellRail, useCurrentModeKey } from "./rail-items";
import { useShellState } from "./shell-state";
import { SidePanel, SidePanelToggle } from "./side-panel";
import { OperatorSidebar } from "./sidebars/operator";
import { FINANCE_ROWS } from "./sidebars/reports";
import { companyLanding } from "./switcher-model";
import { CompanyBreadcrumb, OperatorBreadcrumb, TopBar } from "./top-bar";

/** Signs out, drops every cached query and goes to /login (also the forced MFA screen's). */
export function useSignOut(api: Api): () => void {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return () => {
    // HARDEN-pwa: this browser stops receiving the person's push notifications (a shared
    // machine must not show them to whoever signs in next); the server drops the row on 410.
    if (browserPush.support() === "supported") void browserPush.unsubscribe().catch(() => null);
    void api.auth.signOut().then(async () => {
      queryClient.clear();
      await navigate({ to: "/login" });
    });
  };
}

/** `g p` for agency users: the operator console, from anywhere. */
function useOperatorShortcut(me: MeResponse): GoShortcut[] {
  const router = useRouter();
  return isOperator(me)
    ? [
        {
          key: "p",
          label: "Operator console",
          run: () => void router.navigate({ to: "/operator" }),
        },
      ]
    : [];
}

const asCommands = (shortcuts: readonly GoShortcut[]): CommandMode[] =>
  shortcuts.map((s) => ({ key: s.key, label: s.label, keys: `g ${s.key}`, run: s.run }));

/** Shell for screens outside a company with no rail (company picker, unknown company). */
export function PlainShell({
  me,
  api,
  current,
  main = "doc",
  children,
}: {
  me: MeResponse;
  api: Api;
  current: string | null;
  /** `full` for a screen with its own location header (Account, Setup UX pass 2026-10-21). */
  main?: "doc" | "full";
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const shell = useShellState();
  const signOut = useSignOut(api);
  const operator = useOperatorShortcut(me);
  useGoShortcuts(operator, !shell.presenting);
  return (
    <AppShell
      main={main}
      topbar={
        <TopBar
          slots={{
            companySwitcher: <CompanySwitcher me={me} current={current} />,
            command: (
              <CommandMenu
                modes={asCommands(operator)}
                companies={companiesOf(me)}
                onCompany={(slug) => void navigate({ to: "/c/$slug", params: { slug } })}
              />
            ),
            me: <MeMenu me={me} onSignOut={signOut} />,
          }}
        />
      }
    >
      {children}
    </AppShell>
  );
}

/** Shell for the operator console (`/operator`, `/operator/gate`): agency users only. */
export function OperatorShell({
  me,
  api,
  children,
}: {
  me: MeResponse;
  api: Api;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const shell = useShellState();
  const signOut = useSignOut(api);
  const operator = useOperatorShortcut(me);
  const mobile = useIsMobile();
  useGoShortcuts(operator, !shell.presenting);
  const account = <MeMenu me={me} onSignOut={signOut} variant={mobile ? "topbar" : "rail"} />;
  return (
    <AppShell
      // Every console page is a 56 px location header over tiles and a table (Reports / Ops UX
      // pass 2026-10-21), edge to edge like Work.
      main="full"
      rail={<ShellRail me={me} company={null} account={mobile ? null : account} />}
      sidebar={<OperatorSidebar me={me} />}
      topbar={
        <TopBar
          slots={{
            companySwitcher: <CompanySwitcher me={me} current={null} />,
            breadcrumb: <OperatorBreadcrumb />,
            command: (
              <CommandMenu
                modes={asCommands(operator)}
                companies={companiesOf(me)}
                onCompany={(slug) => void navigate({ to: "/c/$slug", params: { slug } })}
              />
            ),
            me: mobile ? account : null,
          }}
        />
      }
    >
      {children}
    </AppShell>
  );
}

/** Shell for `/c/$slug/…`: rail, mode tabs, mode sidebar, focus chips, keys and ⌘K. */
export function CompanyShell({
  me,
  api,
  company,
  children,
}: {
  me: MeResponse;
  api: Api;
  company: CompanyAccess;
  children: ReactNode;
}) {
  return (
    <FocusProvider company={company}>
      <QuickAddHost api={api} company={company}>
        {/* ADMIN-INSIGHTS: Ask, for owners and admins (nothing for members). */}
        <AskHost api={api} company={company}>
          <CompanyShellInner me={me} api={api} company={company}>
            {children}
          </CompanyShellInner>
        </AskHost>
      </QuickAddHost>
    </FocusProvider>
  );
}

function CompanyShellInner({
  me,
  api,
  company,
  children,
}: {
  me: MeResponse;
  api: Api;
  company: CompanyAccess;
  children: ReactNode;
}) {
  const router = useRouter();
  const navigate = useNavigate();
  const shell = useShellState();
  const signOut = useSignOut(api);
  const operator = useOperatorShortcut(me);
  const mobile = useIsMobile();
  const { focus } = useFocus();
  const quickAdd = useQuickAdd();
  const ask = useAsk();
  const params = { slug: company.slug };

  // Only modes the user can open get a key, a chord and a palette entry (locked ones do not).
  // Every switch lands through `modeLanding`, so the playhead carries.
  const open = useMemo(
    () => shownModes(me, company).filter((i) => i.state !== "locked"),
    [me, company],
  );
  const switchTo = (key: string) => {
    const mode = modeByKey(key);
    if (mode) void router.navigate(modeLanding(mode, focus) as never);
  };
  const shortcuts: GoShortcut[] = [
    ...open.map(({ mode }) => ({
      key: mode.shortcut,
      label: mode.label,
      run: () => switchTo(mode.key),
    })),
    ...operator,
  ];
  useGoShortcuts(shortcuts, !shell.presenting);
  const digits: ModeKey[] = open.flatMap(({ mode }) =>
    mode.digit ? [{ digit: mode.digit, run: () => switchTo(mode.key) }] : [],
  );
  useModeKeys(digits, !shell.presenting);
  const commands: CommandMode[] = [
    ...open.map(({ mode }) => ({
      key: mode.key,
      label: mode.label,
      keys: mode.digit ?? `g ${mode.shortcut}`,
      run: () => switchTo(mode.key),
    })),
    ...asCommands(operator),
  ];

  const go = (to: string) => () =>
    void router.navigate({
      to: to as never,
      params: params as never,
      search: focusSearch(focus) as never,
    });
  /** A page with search params of its own (a command opening a dialog), plus the playhead. */
  const goWith = (to: string, extra: Record<string, string>) => () =>
    void router.navigate({
      to: to as never,
      params: params as never,
      search: { ...focusSearch(focus), ...extra } as never,
    });
  const kpi = featureState(me, company.id, "kpi").enabled;
  const memoryOn = featureState(me, company.id, "memory").enabled;
  const setupOpen = open.some((i) => i.mode.key === "setup") && isManager(company);
  // SEARCH-NOTIF: commands — the actions people look for by name (review item 14). PARITY-0: they
  // are registrations (command-registry.ts; today's in default-commands.ts).
  const commandRows: CommandPage[] = commandPages({
    me,
    company,
    go: (to, search) => (search ? goWith(to, search) : go(to))(),
    ...(quickAdd ? { quickAdd } : {}),
    setupOpen,
    features: { kpi, memory: memoryOn },
  });
  // SETUP-REGROUP: every Setup page from the Setup catalog (the sidebar's list), found by its
  // label, group, description and old names; then data entry and Chart › Finance, which live
  // elsewhere.
  const pages: CommandPage[] = setupOpen
    ? [
        ...commandRows,
        ...setupEntries({ kpi, memory: memoryOn })
          .filter((e) => !e.mode)
          .map((e) => ({
            key: e.key,
            label: e.label,
            keywords: `${e.description} ${e.keywords ?? ""}`,
            run: ((hash) =>
              hash
                ? () =>
                    void router.navigate({
                      to: e.to as never,
                      params: params as never,
                      search: focusSearch(focus) as never,
                      hash,
                    })
                : e.search
                  ? goWith(e.to, e.search)
                  : go(e.to))(e.hash),
          })),
        // Data entry (in KPIs).
        { key: "hours", label: "Enter hours", group: "KPIs", run: go("/c/$slug/kpis/entry/hours") },
        { key: "import", label: "Import CSV", group: "KPIs", run: go("/c/$slug/kpis/import") },
        // M5-04: Reports › Finance (KPIS-MODE: moved from Chart).
        ...FINANCE_ROWS.map((r) => ({
          key: `finance-${r.to}`,
          label: r.label,
          group: "Reports › Finance",
          run: go(r.to),
        })),
      ]
    : [...commandRows];
  // ADMIN-INSIGHTS: ⌘K › Ask… (owners and admins; `useAsk()` is null for anyone else).
  if (ask)
    pages.push({
      key: "ask",
      label: "Ask…",
      group: "Ask",
      keywords: "insights question spend budget tasks numbers how many",
      run: () => ask(),
    });
  // SETUP-REGROUP: Your settings, for everyone (Notifications is SEARCH-NOTIF's command above).
  pages.push({
    key: "appearance",
    label: "Appearance",
    group: "Your settings",
    run: () => void router.navigate({ to: "/settings/appearance" }),
  });
  // BYO-01: everyone's own keys, connections and channel subscriptions.
  pages.push({
    key: "my-subscriptions",
    label: "My subscriptions",
    group: "Your settings",
    run: go("/c/$slug/subscriptions"),
  });
  // KPIS-MODE: the KPIs pages, each for whoever its gate lets in (`chart`: every member;
  // `cadence`: the KPI readers, a member its own pages).
  const chartOpen = canOpen("chart", me, company);
  const scorecardOpen = canOpen("cadence", me, company);
  const minePagesOpen = canOpen("cadence", me, company, true);
  if (chartOpen)
    pages.push({
      key: "kpis-chart",
      label: "Chart",
      group: "KPIs",
      run: go("/c/$slug/kpis/chart"),
    });
  if (scorecardOpen)
    pages.push({
      key: "kpis-scorecard",
      label: "Scorecard",
      group: "KPIs",
      keywords: "grid numbers weeks eos measurables",
      run: go("/c/$slug/kpis/scorecard"),
    });
  if (minePagesOpen)
    pages.push(
      {
        key: "my-numbers",
        label: "My numbers",
        group: "KPIs",
        run: go("/c/$slug/kpis/my-numbers"),
      },
      {
        key: "my-scorecard",
        label: "My scorecard",
        group: "KPIs",
        run: goWith("/c/$slug/kpis/my-numbers", { tab: "scorecard" }),
      },
    );
  // PRIORITIES-HOME, MEETINGS: the quarter's priorities and the operating cadence's pages, for
  // everyone who opens the scorecard or the chart.
  if (chartOpen || scorecardOpen)
    pages.push(
      {
        key: "priorities",
        label: "Priorities",
        group: "KPIs",
        run: go("/c/$slug/kpis/priorities"),
      },
      { key: "issues", label: "Issues", group: "KPIs", run: go("/c/$slug/kpis/issues") },
      { key: "meetings", label: "Meetings", group: "KPIs", run: go("/c/$slug/kpis/meetings") },
      { key: "vision", label: "Vision", group: "KPIs", run: go("/c/$slug/kpis/vision") },
      // DE-KPIS-LIVE
      {
        key: "de-kpis",
        label: "DE KPIs",
        group: "KPIs",
        keywords: "digital employees agents helpers runs cost spend budget memory",
        run: go("/c/$slug/kpis/agents"),
      },
    );

  // UX-A 0.8: the Setup catalog's pages in other modes (↗ in the Setup search) under their
  // mode's group, unless that group already lists the page by name.
  if (setupOpen)
    for (const e of setupEntries({ kpi, memory: memoryOn })) {
      if (!e.mode) continue;
      const group = MODE_LABEL[e.mode];
      if (pages.some((p) => p.label === e.label && p.group === group)) continue;
      pages.push({
        key: `setup-${e.key}`,
        label: e.label,
        group,
        keywords: `${e.description} ${e.keywords ?? ""}`,
        run: go(e.to),
      });
    }

  // The sidebar is the current mode's panel, when the user can open that mode (KPIs is `core`
  // and everyone's: its sidebar shows each viewer the rows its gates let in).
  const modeKey = useCurrentModeKey();
  // The side panel belongs to the mode it was opened in (Chat's thread and memory, the chart's
  // seat drawer): a mode switch closes it. ⌘K is global and stays (the inbox peek closes on any
  // navigation).
  const { setPanelOpen, setPanelTab } = shell;
  const lastMode = useRef(modeKey);
  useEffect(() => {
    if (lastMode.current === modeKey) return;
    lastMode.current = modeKey;
    setPanelOpen(false);
    setPanelTab(null);
  }, [modeKey, setPanelOpen, setPanelTab]);
  const mode = modeKey ? modeByKey(modeKey) : undefined;
  const sidebarMode = mode && modeAccess(mode, me, company) === "ok" ? mode : undefined;
  const ModeSidebar = sidebarMode?.sidebar ?? null;
  const apps = me.dock?.[company.id]?.items ?? [];
  // NAV-REDESIGN: Me sits at the rail's foot; on a phone, at the end of the bottom bar.
  const account = (
    <MeMenu me={me} company={company} onSignOut={signOut} variant={mobile ? "bottom" : "rail"} />
  );
  // A chat channel, every Work, Agents and Reports screen (Reports: the weeks, Finance), KPIs'
  // chart, scorecard, priorities, issues, meetings and vision, the Setup screens (Teams, Seats and Finance are Chart's editors) and My
  // subscriptions fill the main area edge to edge (a 56 px location header over a board, canvas,
  // list, table or settings column; Work UX pass 2026-10-20, Agents, Chart and Setup UX passes
  // 2026-10-21); every other page is the centred document frame. Setup › Model policy keeps the
  // frame until its own pass lands (wp/MODEL-POLICY).
  const { channelId } = useParams({ strict: false }) as { channelId?: string };
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const tabSearch = useRouterState({
    select: (s) => (s.location.search as { tab?: unknown }).tab,
  });
  const focusControls = focusControlsAt(pathname, { tab: tabSearch });
  const fullBleed =
    (modeKey === "chat" && !!channelId) ||
    modeKey === "work" ||
    modeKey === "agents" ||
    // CALENDAR-C1: the time grid fills the main area under its own location header.
    modeKey === "calendar" ||
    // SCREENS-VIEWER: the sessions list and the live screen fill the main area, under Live's
    // own location header.
    modeKey === "live" ||
    // NAV-REDESIGN: KPIs › Reports (the weeks, Finance) keeps Reports' edge-to-edge pages.
    /^\/c\/[^/]+\/kpis\/(chart|scorecard|priorities|issues|meetings|vision|reports)(\/|$)/.test(
      pathname,
    ) ||
    // FOUNDER-OS: every Portfolio page has its own location header.
    modeKey === "portfolio" ||
    (modeKey === "setup" && !/^\/c\/[^/]+\/setup\/model-policy(\/|$)/.test(pathname)) ||
    /^\/c\/[^/]+\/(subscriptions|notifications|inbox)\/?$/.test(pathname);

  return (
    <AppShell
      main={fullBleed ? "full" : "doc"}
      // SIDEBAR-TOGGLE-FIXED: each mode remembers whether its sidebar is collapsed.
      {...(modeKey ? { sidebarKey: modeKey } : {})}
      rail={
        <ShellRail
          me={me}
          company={company}
          account={mobile ? null : account}
          inbox={<InboxRailItem api={api} company={company} />}
        />
      }
      sidebar={
        ModeSidebar ? (
          <ModeSidebar company={company}>
            {/* Pinned apps sit under the mode's rows. */}
            {apps.length > 0 ? <SidebarApps items={apps} /> : null}
          </ModeSidebar>
        ) : null
      }
      topbar={
        <TopBar
          slots={{
            companySwitcher: <CompanySwitcher me={me} current={company.slug} />,
            tabs: <ShellModeTabs me={me} company={company} />,
            // INBOX-READABLE: the Inbox page has no mode, so no crumb row (it left an empty band).
            breadcrumb: isInboxPage(pathname) ? null : <CompanyBreadcrumb slug={company.slug} />,
            // SETUP-REGROUP (review #19): the scope and week controls only where a screen reads
            // them (KPIs, Reports, a DE's scorecard); Chat, Work, Setup… have none.
            chips: focusControls ? (
              <FocusChips scope={focusControls.scope} week={focusControls.week ?? true} />
            ) : null,
            command: (
              <CommandMenu
                compact
                modes={commands}
                pages={pages}
                // WOS-BUNDLE: server recents, and link commands while a task is open, first.
                lead={(query, pick) => (
                  <WorkPaletteExtras api={api} company={company} query={query} onPick={pick} />
                )}
                search={(query, pick) => (
                  <PaletteHits api={api} company={company} query={query} onPick={pick} />
                )}
                companies={companiesOf(me)}
                // FOUNDER-OS: the switcher's rule — the mode stays when the target shows it.
                onCompany={(slug) => {
                  const target = companiesOf(me).find((c) => c.slug === slug);
                  if (!target) return;
                  const landing = companyLanding(me, target, modeKey);
                  void navigate({ to: landing.to as never, params: landing.params as never });
                }}
              />
            ),
            timer: <TimerChip api={api} company={company} />,
            mobileApps: apps.length > 0 ? <MobileAppsMenu items={apps} /> : null,
            panel: <SidePanelToggle />,
          }}
        />
      }
      panel={<SidePanel />}
      bottomBar={
        <MobileModeBar
          me={me}
          company={company}
          inbox={<InboxBottomItem api={api} company={company} />}
          account={mobile ? account : null}
        />
      }
    >
      {/* M5-05: a live or waiting agency break-glass session, for the company's managers. */}
      {/* On an edge-to-edge page the banner takes the page frame's gutters itself. */}
      <div className={fullBleed ? "px-6 pt-4 empty:hidden max-md:px-4" : "contents"}>
        <BreakGlassBanner api={api} company={company} userId={me.user.id} />
      </div>
      {/* GUIDED-FLOW: "Complete your seat setup, 3 of 5 chapters" while the caller has one open. */}
      <FlowBanner api={api} company={company} />
      {children}
      {/* SEARCH-NOTIF: the inbox items that ask for a desktop notification. */}
      <DesktopNotifier api={api} company={company} />
      {/* HARDEN-pwa: a push notification's click opens this company's inbox. */}
      <PushOpen />
    </AppShell>
  );
}
